import { Component, EventEmitter, Input, OnChanges, OnDestroy, Output, SimpleChanges } from '@angular/core';

import { Unsubscribable } from 'rxjs';

import {
  CompoundFilter,
  Filter,
  findLeafByOn,
  LeafFilter,
  Range,
  RangeReferencePoint,
  RangeReferenceUnit,
  RangeUtil
} from '@itonics/filters';

import { TranslateService } from '@itonics/ngx-translate-simple';

import { CUSTOM_RANGE, Ui2DatePickerModel, Ui2DatePickerType, Ui2QuickSelectOption } from '@innovation-os-ng/ui/ui2-datepicker';
import { TrackedEvents } from '../../../../constants/tracker-ids';
import { compareLeafByOnAndExclude } from '../../../../helpers/filter-helper';
import { DatePickerAlignment, SignalsFilters, SignalsType } from '../../../../model/types';
import { InsightsTrackingService } from '../../../shared-services/services/insights-tracking-service';
import { DatePickerOptionsId } from '../../constants/constants';

@Component({
  selector: 'signals-filter-date',
  templateUrl: './filter-date.component.html',
  styleUrls: ['./filter-date.component.scss']
})
export class FilterDateComponent implements OnDestroy, OnChanges {
  public selectionFilter: CompoundFilter;
  @Input() datePickerAlignment = DatePickerAlignment.BOTTOM;
  @Input() isStyled = false;
  @Input() customClass: string = '';
  @Input() placeholder: string;

  /**
   * Receives the flag whether to hide
   * the cross button when any option
   * is selected.
   */
  @Input()
  public hideCrossButton: boolean = false;

  /**
   * Receives the list of quick option id's to hide.
   */
  @Input()
  public hideQuickOptionListIds: Array<string> = [];

  @Input()
  set globalFilter(filter: CompoundFilter) {
    this.selectionFilter = filter?.getQuery(SignalsFilters.SELECTION) as CompoundFilter;
    this.unsubscribe();
    this.subscribe();
    this.autoFillFromFilter();
  }

  /**
   * We notify the filter change to the parent component.
   */
  @Output()
  public onSelectionFilterChange: EventEmitter<CompoundFilter> = new EventEmitter<CompoundFilter>();

  public rangeDateSelectorModel: Ui2DatePickerModel | Ui2QuickSelectOption;
  private contextFilterChanges: Unsubscribable;

  public defaultDate = {
    jsdate: new Date(),
    endJsDate: new Date(new Date().getFullYear(), new Date().getMonth() + 5, new Date().getDate())
  };

  // public the enum
  public ui2DatePickerType = Ui2DatePickerType;

  public quickSelectOptions: Array<Ui2QuickSelectOption> = [
    {
      id: DatePickerOptionsId.LAST_7_DAYS,
      label: this.translate.instant('signals-ng-filter-date.searchField.contextSearch.quickSelection.pastSevenDays'),
      msFromNow: 7 * 24 * 60 * 60 * 100,
      onSelect: () => {
        const filter = new LeafFilter(SignalsType.PUBLISHED_DATE);
        filter.setRange({
          gt: {
            value: -7,
            reference: RangeReferencePoint.DATE_NOW,
            unit: RangeReferenceUnit.DAYS
          },
          lt: {
            value: 0,
            reference: RangeReferencePoint.DATE_NOW,
            unit: RangeReferenceUnit.DAYS
          }
        });
        this.updateDateFilter(filter, 'pastSevenDays');
      }
    },
    {
      id: DatePickerOptionsId.PAST_1_MONTH,
      label: this.translate.instant('signals-ng-filter-date.searchField.contextSearch.quickSelection.pastOneMonth'),
      msFromNow: 30 * 24 * 60 * 60,
      onSelect: () => {
        const filter = new LeafFilter(SignalsType.PUBLISHED_DATE);
        filter.setRange({
          gt: {
            value: -1,
            reference: RangeReferencePoint.DATE_NOW,
            unit: RangeReferenceUnit.MONTHS
          },
          lt: {
            value: 0,
            reference: RangeReferencePoint.DATE_NOW,
            unit: RangeReferenceUnit.DAYS
          }
        });
        this.updateDateFilter(filter, 'pastOneMonth');
      }
    },
    {
      id: DatePickerOptionsId.PAST_1_QUARTER,
      label: this.translate.instant('signals-ng-filter-date.searchField.contextSearch.quickSelection.pastOneQuarter'),
      msFromNow: 30 * 24 * 60 * 60,
      onSelect: () => {
        const filter = new LeafFilter(SignalsType.PUBLISHED_DATE);
        filter.setRange({
          gt: {
            value: -3,
            reference: RangeReferencePoint.DATE_NOW,
            unit: RangeReferenceUnit.MONTHS
          },
          lt: {
            value: 0,
            reference: RangeReferencePoint.DATE_NOW,
            unit: RangeReferenceUnit.DAYS
          }
        });
        this.updateDateFilter(filter, 'pastOneQuarter');
      }
    },
    {
      id: DatePickerOptionsId.PAST_1_YEAR,
      label: this.translate.instant('signals-ng-filter-date.searchField.contextSearch.quickSelection.pastOneYear'),
      msFromNow: 30 * 24 * 60 * 60,
      onSelect: () => {
        const filter = new LeafFilter(SignalsType.PUBLISHED_DATE);
        filter.setRange({
          gt: {
            value: -1,
            reference: RangeReferencePoint.DATE_NOW,
            unit: RangeReferenceUnit.YEARS
          },
          lt: {
            value: 0,
            reference: RangeReferencePoint.DATE_NOW,
            unit: RangeReferenceUnit.DAYS
          }
        });
        this.updateDateFilter(filter, 'pastOneYear');
      }
    },
    {
      id: DatePickerOptionsId.PAST_2_YEAR,
      label: this.translate.instant('signals-ng-filter-date.searchField.contextSearch.quickSelection.pastTwoYear'),
      msFromNow: 30 * 24 * 60 * 60,
      onSelect: () => {
        const filter = new LeafFilter(SignalsType.PUBLISHED_DATE);
        filter.setRange({
          gt: {
            value: -2,
            reference: RangeReferencePoint.DATE_NOW,
            unit: RangeReferenceUnit.YEARS
          },
          lt: {
            value: 0,
            reference: RangeReferencePoint.DATE_NOW,
            unit: RangeReferenceUnit.DAYS
          }
        });
        this.updateDateFilter(filter, 'pastTwoYear');
      }
    },
    {
      id: DatePickerOptionsId.PAST_5_YEAR,
      label: this.translate.instant('signals-ng-filter-date.searchField.contextSearch.quickSelection.pastFiveYear'),
      msFromNow: 30 * 24 * 60 * 60,
      onSelect: () => {
        const filter = new LeafFilter(SignalsType.PUBLISHED_DATE);
        filter.setRange({
          gt: {
            value: -5,
            reference: RangeReferencePoint.DATE_NOW,
            unit: RangeReferenceUnit.YEARS
          },
          lt: {
            value: 0,
            reference: RangeReferencePoint.DATE_NOW,
            unit: RangeReferenceUnit.DAYS
          }
        });
        this.updateDateFilter(filter, 'pastFiveYear');
      }
    },
    {
      id: DatePickerOptionsId.FROM_START_OF_MONTH,
      label: this.translate.instant('signals-ng-filter-date.searchField.contextSearch.quickSelection.fromStartOfMonth'),
      msFromNow: 30 * 24 * 60 * 60,
      onSelect: () => {
        const filter = new LeafFilter(SignalsType.PUBLISHED_DATE);
        filter.setRange({
          gt: {
            value: 0,
            reference: RangeReferencePoint.DATE_MONTH_START,
            unit: RangeReferenceUnit.DAYS
          },
          lt: {
            value: 0,
            reference: RangeReferencePoint.DATE_NOW,
            unit: RangeReferenceUnit.DAYS
          }
        });
        this.updateDateFilter(filter, 'fromStartOfMonth');
      }
    },
    {
      id: DatePickerOptionsId.FROM_START_OF_YEAR,
      label: this.translate.instant('signals-ng-filter-date.searchField.contextSearch.quickSelection.fromStartOfYear'),
      msFromNow: 30 * 24 * 60 * 60,
      onSelect: () => {
        const filter = new LeafFilter(SignalsType.PUBLISHED_DATE);
        filter.setRange({
          gt: {
            value: 0,
            reference: RangeReferencePoint.DATE_YEAR_START,
            unit: RangeReferenceUnit.DAYS
          },
          lt: {
            value: 0,
            reference: RangeReferencePoint.DATE_NOW,
            unit: RangeReferenceUnit.DAYS
          }
        });
        this.updateDateFilter(filter, 'fromStartOfYear');
      }
    }
  ];

  constructor(private readonly translate: TranslateService, private readonly trackingService: InsightsTrackingService) {}

  /**
   * Listen to input changes.
   *
   * @param changes - Simple change object
   */
  public ngOnChanges(changes: SimpleChanges): void {
    if (changes?.hideQuickOptionListIds) {
      // Quick options to hide from list.
      // Done to hide 5 years for normalized data.
      this.quickSelectOptions = this.quickSelectOptions.filter((option) => !this.hideQuickOptionListIds.includes(option?.id));
    }
  }

  ngOnDestroy() {
    this.unsubscribe();
  }

  private subscribe() {
    this.contextFilterChanges = this?.selectionFilter?.onChanged((updatedFilter) => {
      if (updatedFilter.getIdentifier() === this.selectionFilter.getIdentifier()) {
        // our very own filter changed
        this.autoFillFromFilter();

        // We emit changed filter to the parent component.
        this.onSelectionFilterChange.emit(this.selectionFilter);
      }
    });
  }

  private unsubscribe() {
    if (this.contextFilterChanges) {
      this.contextFilterChanges.unsubscribe();
    }
  }

  /**
   * Update the date filter in the global selection filter
   *
   * @param filter LeafFilter to add to selection filter
   * @param label DateFilter preset selected (for analytics)
   */
  private updateDateFilter(filter: LeafFilter, label?: string) {
    this.trackingService.trackEvent(TrackedEvents.DATE_SELECT_RANGE, { dimension4: label });
    this.selectionFilter.addFilter(filter, compareLeafByOnAndExclude);
  }

  // We need to auto select all the data from the search field filter
  private autoFillFromFilter() {
    if (this.selectionFilter) {
      const publishedDateFilter = this.selectionFilter
        .getQueries()
        .find((filter) => filter instanceof LeafFilter && filter.getOn() === SignalsType.PUBLISHED_DATE) as LeafFilter;

      if (!publishedDateFilter) {
        this.rangeDateSelectorModel = null;

        this.removePublishedDate();
        return;
      }

      const rangeRef: Range = publishedDateFilter.getRange();
      this.rangeDateSelectorModel = {
        label: CUSTOM_RANGE
      } as Ui2QuickSelectOption;

      if (rangeRef.lt && rangeRef.lt.reference && rangeRef.lt && rangeRef.lt.reference) {
        this.select(
          {
            endJsDate: new Date(RangeUtil.convert(rangeRef.lt)),
            beginJsDate: new Date(RangeUtil.convert(rangeRef.gt))
          },
          false
        );
        const selectedID = `${rangeRef.gt.value}${rangeRef.gt.unit}${rangeRef.gt.reference}`;
        if (this.quickSelectOptions.find((opt) => opt.id === selectedID)) {
          this.rangeDateSelectorModel = this.quickSelectOptions.find((opt) => opt.id === selectedID);
        }
        return;
      }
      this.select(
        {
          endJsDate: new Date(publishedDateFilter.getRange().lt.value),
          beginJsDate: new Date(publishedDateFilter.getRange().gt.value)
        },
        false
      );
    }
  }

  public select(option: Ui2DatePickerModel | Ui2QuickSelectOption, addAsFilter: boolean = true) {
    if (!option) {
      this.clearPublishedDate();
      return;
    }

    const dateModel: Ui2DatePickerModel = option as Ui2DatePickerModel;
    if (!dateModel.beginJsDate || !dateModel.endJsDate) {
      (option as Ui2QuickSelectOption)?.onSelect();

      return;
    }

    const startDate = dateModel.beginJsDate.getTime();
    const endDate = this.getEndOfTheDay(dateModel.endJsDate.getTime());
    const filter = new LeafFilter(SignalsType.PUBLISHED_DATE);
    filter.setRange({
      gt: { value: startDate }, // example
      lt: { value: endDate } // example
    });

    this.rangeDateSelectorModel = {
      beginJsDate: new Date(startDate),
      endJsDate: new Date(endDate)
    };
    if (addAsFilter) {
      /*
      We want here to add/replace the LeafFilter that doesn't have an id,
      so we need to provide compareLeafByOnAndExclude that helps find the leafFilter
    */
      this.selectionFilter.addFilter(filter, compareLeafByOnAndExclude);
    }
  }

  private getEndOfTheDay(date: number): number {
    const end = new Date(date);
    end.setHours(23, 59, 59, 999);

    return end.getTime();
  }

  /**
   * Push the groupUri to the method and it will update the filter with all selected value of provided group
   */
  public updateFilter(data, groupUri, bindValue?: string) {
    const uris = data.map((d) => (bindValue ? d[bindValue] : d.uri));
    if (uris.length) {
      const filter: LeafFilter = new LeafFilter(groupUri).setValues(uris);
      filter.setIdentifier(groupUri);
      this.selectionFilter.addFilter(filter, compareLeafByOnAndExclude);
      return;
    }
    const filterRef = this.selectionFilter.findQuery((filter: LeafFilter) => filter.getOn() === groupUri);
    if (filterRef) {
      this.selectionFilter.removeFilter(filterRef);
    }
  }

  public clearPublishedDate() {
    this.selectionFilter.removeFilters((filter: Filter) =>
      filter instanceof LeafFilter ? filter.getOn() === SignalsType.PUBLISHED_DATE : filter.getIdentifier() === SignalsType.PUBLISHED_DATE
    );
    this.rangeDateSelectorModel = null;
  }

  public removePublishedDate() {
    const addedFilter = this.selectionFilter.findQuery(findLeafByOn(SignalsType.PUBLISHED_DATE));
    if (addedFilter) {
      this.selectionFilter.removeFilter(addedFilter);
    }
    this.rangeDateSelectorModel = null;
  }

  /**
   * Track the given event (insights.filterPresets. prefix will be added)
   *
   * @param name Event name
   */
  public trackEvent(name: string) {
    this.trackingService.trackEvent(`${TrackedEvents.PREFIX_DATE_FILTER}.${name}`);
  }
}
